import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Modal,
  RefreshControl,
} from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { COLORS, FONTS } from '../../constants/config';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { PageContainer, Breadcrumb } from '../../components';
import { partnersApi, Partner, PartnerType } from '../../services/apiService';

const PARTNER_TYPES: { value: PartnerType; label: string }[] = [
  { value: 'government', label: 'جهة حكومية' },
  { value: 'private', label: 'قطاع خاص' },
  { value: 'non_profit', label: 'منظمة غير ربحية' },
  { value: 'academic', label: 'مؤسسة أكاديمية' },
  { value: 'international', label: 'منظمة دولية' },
  { value: 'other', label: 'أخرى' },
];

const TYPE_BADGE_COLORS: Record<PartnerType, { bg: string; fg: string }> = {
  government:    { bg: '#EFF6FF', fg: '#1E40AF' },
  private:       { bg: '#F0FDF4', fg: '#15803D' },
  non_profit:    { bg: '#FEF3C7', fg: '#92400E' },
  academic:      { bg: '#F3E8FF', fg: '#6B21A8' },
  international: { bg: '#FCE7F3', fg: '#9D174D' },
  other:         { bg: '#F1F5F9', fg: '#334155' },
};

/**
 * إدارة قائمة الشركاء — DTS section 2.1 + UC-03 step 7 + FR-27.
 *
 * Curated catalog. Strategy Team adds external partner organizations
 * (government / private / non-profit / academic / international / other)
 * here so initiative authors can attach them to operational elements
 * (informational link only — no permission grant).
 */
export default function PartnersScreen() {
  const { showSuccess, showError } = useNotification();
  const { colors } = useTheme();
  const { user } = useAuth();
  const navigation = useNavigation<NativeStackNavigationProp<any>>();

  const canEdit = !!user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.strategic_goals.create');

  const [items, setItems] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [editing, setEditing] = useState<Partner | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formName, setFormName] = useState('');
  const [formNameEn, setFormNameEn] = useState('');
  const [formType, setFormType] = useState<PartnerType>('government');
  const [formDescription, setFormDescription] = useState('');
  const [formContactPerson, setFormContactPerson] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPhone, setFormPhone] = useState('');
  const [formWebsite, setFormWebsite] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);
  const [saving, setSaving] = useState(false);

  const [filterType, setFilterType] = useState<PartnerType | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const params: Record<string, any> = { page_size: 200 };
      if (filterType !== 'all') params.partner_type = filterType;
      if (searchTerm.trim()) params.search = searchTerm.trim();
      const resp = await partnersApi.list(params);
      setItems(resp.results || []);
    } catch (err) {
      console.error('Failed to load partners:', err);
      showError('تعذّر تحميل قائمة الشركاء');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filterType, searchTerm, showError]);

  useFocusEffect(useCallback(() => { fetchData(); }, [fetchData]));

  const openAdd = () => {
    setEditing(null);
    setFormName('');
    setFormNameEn('');
    setFormType('government');
    setFormDescription('');
    setFormContactPerson('');
    setFormEmail('');
    setFormPhone('');
    setFormWebsite('');
    setFormIsActive(true);
    setShowForm(true);
  };

  const openEdit = (item: Partner) => {
    setEditing(item);
    setFormName(item.name);
    setFormNameEn(item.name_en || '');
    setFormType(item.partner_type);
    setFormDescription(item.description || '');
    setFormContactPerson(item.contact_person || '');
    setFormEmail(item.email || '');
    setFormPhone(item.phone || '');
    setFormWebsite(item.website || '');
    setFormIsActive(item.is_active);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      showError('اسم الشريك إجباري');
      return;
    }
    setSaving(true);
    try {
      const payload: Partial<Partner> = {
        name: formName.trim(),
        name_en: formNameEn.trim(),
        partner_type: formType,
        description: formDescription.trim(),
        contact_person: formContactPerson.trim(),
        email: formEmail.trim(),
        phone: formPhone.trim(),
        website: formWebsite.trim(),
        is_active: formIsActive,
      };
      if (editing) {
        await partnersApi.update(editing.id, payload);
        showSuccess('تم تحديث بيانات الشريك');
      } else {
        await partnersApi.create(payload);
        showSuccess('تم إضافة الشريك');
      }
      setShowForm(false);
      await fetchData();
    } catch (err: any) {
      showError(err?.response?.data?.detail || 'فشل الحفظ');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: Partner) => {
    if (!window.confirm(`حذف الشريك "${item.name}"؟`)) return;
    try {
      await partnersApi.delete(item.id);
      showSuccess('تم الحذف');
      await fetchData();
    } catch (err: any) {
      showError(err?.response?.data?.detail || 'فشل الحذف');
    }
  };

  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textMuted: colors.textMuted,
    inputBackground: colors.inputBackground,
    border: colors.border,
  };

  return (
    <PageContainer>
      <ScrollView
        contentContainerStyle={{ padding: 16, gap: 16 }}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); fetchData(); }} />
        }
      >
        <Breadcrumb items={[{ label: 'الرئيسية', icon: 'home' as any }, { label: 'الشركاء' }]} />

        <View style={[styles.headerCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
          <View style={{ flex: 1 }}>
            <Text style={[styles.title, { color: themeColors.textPrimary }]}>الشركاء</Text>
            <Text style={[styles.subtitle, { color: themeColors.textMuted }]}>
              قائمة الجهات الشريكة — تُربط لاحقا بالعناصر التشغيلية للعلم فقط (لا تمنح أي صلاحيات).
            </Text>
          </View>
          {canEdit && (
            <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
              <TouchableOpacity
                style={styles.importButton}
                onPress={() => navigation.navigate('ImportPartners')}
              >
                <Text style={styles.importButtonText}>استيراد Excel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.addButton} onPress={openAdd}>
                <Text style={styles.addButtonText}>+ إضافة شريك</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        <View style={[styles.filterBar, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
          <TextInput
            value={searchTerm}
            onChangeText={setSearchTerm}
            placeholder="بحث بالاسم..."
            placeholderTextColor={themeColors.textMuted}
            style={[styles.searchInput, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
            textAlign="right"
          />
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ flexDirection: 'row-reverse', gap: 8 }}>
            <TouchableOpacity
              style={[styles.filterChip, filterType === 'all' && styles.filterChipActive]}
              onPress={() => setFilterType('all')}
            >
              <Text style={[styles.filterChipText, filterType === 'all' && styles.filterChipTextActive]}>الكل</Text>
            </TouchableOpacity>
            {PARTNER_TYPES.map(({ value, label }) => (
              <TouchableOpacity
                key={value}
                style={[styles.filterChip, filterType === value && styles.filterChipActive]}
                onPress={() => setFilterType(value)}
              >
                <Text style={[styles.filterChipText, filterType === value && styles.filterChipTextActive]}>{label}</Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>

        {loading ? (
          <View style={{ padding: 40, alignItems: 'center' }}>
            <ActivityIndicator size="large" color={COLORS.primary} />
          </View>
        ) : items.length === 0 ? (
          <View style={[styles.emptyCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <Text style={[styles.emptyText, { color: themeColors.textMuted }]}>
              لا يوجد شركاء بعد
            </Text>
            {canEdit && (
              <Text style={[styles.emptyHint, { color: themeColors.textMuted }]}>
                ابدأ بإضافة شريك من زر "إضافة شريك" أعلاه.
              </Text>
            )}
          </View>
        ) : (
          <View style={{ gap: 12 }}>
            {items.map((item) => {
              const badge = TYPE_BADGE_COLORS[item.partner_type] || TYPE_BADGE_COLORS.other;
              return (
                <View key={item.id} style={[styles.itemCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder, opacity: item.is_active ? 1 : 0.55 }]}>
                  <View style={{ flex: 1 }}>
                    <View style={{ flexDirection: 'row-reverse', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <View style={[styles.typeBadge, { backgroundColor: badge.bg }]}>
                        <Text style={[styles.typeBadgeText, { color: badge.fg }]}>
                          {item.partner_type_display || PARTNER_TYPES.find(t => t.value === item.partner_type)?.label}
                        </Text>
                      </View>
                      <Text style={[styles.itemTitle, { color: themeColors.textPrimary }]}>{item.name}</Text>
                      {!item.is_active && (
                        <View style={styles.inactiveBadge}>
                          <Text style={styles.inactiveBadgeText}>غير فعّال</Text>
                        </View>
                      )}
                    </View>
                    {item.name_en ? (
                      <Text style={[styles.itemMeta, { color: themeColors.textMuted }]}>{item.name_en}</Text>
                    ) : null}
                    {item.description ? (
                      <Text style={[styles.itemDescription, { color: themeColors.textMuted }]} numberOfLines={2}>{item.description}</Text>
                    ) : null}
                    <View style={{ flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
                      {item.contact_person ? (
                        <Text style={[styles.contactItem, { color: themeColors.textMuted }]}>👤 {item.contact_person}</Text>
                      ) : null}
                      {item.email ? (
                        <Text style={[styles.contactItem, { color: themeColors.textMuted }]}>✉️ {item.email}</Text>
                      ) : null}
                      {item.phone ? (
                        <Text style={[styles.contactItem, { color: themeColors.textMuted }]}>📞 {item.phone}</Text>
                      ) : null}
                    </View>
                    <Text style={[styles.usageCount, { color: themeColors.textMuted }]}>
                      عدد العناصر التشغيلية المرتبطة: {item.initiatives_count ?? 0}
                    </Text>
                  </View>
                  {canEdit && (
                    <View style={{ flexDirection: 'row-reverse', gap: 8 }}>
                      <TouchableOpacity style={styles.editBtn} onPress={() => openEdit(item)}>
                        <Text style={styles.editBtnText}>تعديل</Text>
                      </TouchableOpacity>
                      <TouchableOpacity style={styles.deleteBtn} onPress={() => handleDelete(item)}>
                        <Text style={styles.deleteBtnText}>حذف</Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </View>
              );
            })}
          </View>
        )}
      </ScrollView>

      <Modal visible={showForm} transparent animationType="fade" onRequestClose={() => setShowForm(false)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.modalCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <ScrollView contentContainerStyle={{ padding: 4 }}>
              <Text style={[styles.modalTitle, { color: themeColors.textPrimary }]}>
                {editing ? 'تعديل الشريك' : 'إضافة شريك'}
              </Text>
              <View style={{ gap: 12 }}>
                <View>
                  <Text style={[styles.formLabel, { color: themeColors.textMuted }]}>الاسم *</Text>
                  <TextInput
                    value={formName}
                    onChangeText={setFormName}
                    placeholder="اسم الشريك بالعربية"
                    placeholderTextColor={themeColors.textMuted}
                    style={[styles.input, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
                    textAlign="right"
                  />
                </View>
                <View>
                  <Text style={[styles.formLabel, { color: themeColors.textMuted }]}>الاسم بالإنجليزية</Text>
                  <TextInput
                    value={formNameEn}
                    onChangeText={setFormNameEn}
                    placeholder="Partner name (EN)"
                    placeholderTextColor={themeColors.textMuted}
                    style={[styles.input, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
                    textAlign="left"
                  />
                </View>
                <View>
                  <Text style={[styles.formLabel, { color: themeColors.textMuted }]}>نوع الشريك *</Text>
                  <View style={styles.typeSelector}>
                    {PARTNER_TYPES.map(({ value, label }) => (
                      <TouchableOpacity
                        key={value}
                        style={[styles.typeOption, formType === value && styles.typeOptionActive]}
                        onPress={() => setFormType(value)}
                      >
                        <Text style={[styles.typeOptionText, formType === value && styles.typeOptionTextActive]}>{label}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
                <View>
                  <Text style={[styles.formLabel, { color: themeColors.textMuted }]}>الوصف</Text>
                  <TextInput
                    value={formDescription}
                    onChangeText={setFormDescription}
                    placeholder="وصف موجز للشريك ومجال التعاون"
                    placeholderTextColor={themeColors.textMuted}
                    style={[styles.input, styles.textarea, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
                    textAlign="right"
                    multiline
                  />
                </View>
                <View style={{ flexDirection: 'row-reverse', gap: 12 }}>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.formLabel, { color: themeColors.textMuted }]}>جهة الاتصال</Text>
                    <TextInput
                      value={formContactPerson}
                      onChangeText={setFormContactPerson}
                      placeholder="الاسم"
                      placeholderTextColor={themeColors.textMuted}
                      style={[styles.input, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
                      textAlign="right"
                    />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.formLabel, { color: themeColors.textMuted }]}>الهاتف</Text>
                    <TextInput
                      value={formPhone}
                      onChangeText={setFormPhone}
                      placeholder="+9715XXXXXXXX"
                      placeholderTextColor={themeColors.textMuted}
                      keyboardType="phone-pad"
                      style={[styles.input, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
                      textAlign="left"
                    />
                  </View>
                </View>
                <View style={{ flexDirection: 'row-reverse', gap: 12 }}>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.formLabel, { color: themeColors.textMuted }]}>البريد الإلكتروني</Text>
                    <TextInput
                      value={formEmail}
                      onChangeText={setFormEmail}
                      placeholder="contact@partner.ae"
                      placeholderTextColor={themeColors.textMuted}
                      keyboardType="email-address"
                      autoCapitalize="none"
                      style={[styles.input, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
                      textAlign="left"
                    />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={[styles.formLabel, { color: themeColors.textMuted }]}>الموقع</Text>
                    <TextInput
                      value={formWebsite}
                      onChangeText={setFormWebsite}
                      placeholder="https://..."
                      placeholderTextColor={themeColors.textMuted}
                      autoCapitalize="none"
                      style={[styles.input, { backgroundColor: themeColors.inputBackground, borderColor: themeColors.border, color: themeColors.textPrimary }]}
                      textAlign="left"
                    />
                  </View>
                </View>
                <TouchableOpacity
                  style={styles.activeToggle}
                  onPress={() => setFormIsActive((v) => !v)}
                >
                  <View style={[styles.checkbox, formIsActive && styles.checkboxActive]}>
                    {formIsActive ? <Text style={styles.checkboxMark}>✓</Text> : null}
                  </View>
                  <Text style={[styles.activeToggleLabel, { color: themeColors.textPrimary }]}>
                    شريك فعّال (يظهر في قوائم الاختيار)
                  </Text>
                </TouchableOpacity>
              </View>
              <View style={{ flexDirection: 'row-reverse', gap: 8, marginTop: 16 }}>
                <TouchableOpacity
                  style={[styles.modalSaveBtn, saving && { opacity: 0.5 }]}
                  disabled={saving}
                  onPress={handleSave}
                >
                  <Text style={styles.modalSaveBtnText}>{saving ? '...' : 'حفظ'}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={[styles.modalCancelBtn, { borderColor: themeColors.border }]}
                  onPress={() => setShowForm(false)}
                >
                  <Text style={[styles.modalCancelBtnText, { color: themeColors.textPrimary }]}>إلغاء</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  headerCard: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
  },
  title: { fontSize: 20, fontFamily: FONTS.medium, textAlign: 'right' },
  subtitle: { fontSize: 13, fontFamily: FONTS.regular, textAlign: 'right', marginTop: 4 },
  addButton: {
    paddingVertical: 10, paddingHorizontal: 16,
    backgroundColor: COLORS.primary, borderRadius: 8,
  },
  importButton: {
    paddingVertical: 10, paddingHorizontal: 14,
    backgroundColor: '#FFFFFF', borderRadius: 8,
    borderWidth: 1, borderColor: '#E2E8F0',
  },
  importButtonText: { color: '#0F172A', fontSize: 14, fontFamily: FONTS.medium },
  addButtonText: { color: '#FFFFFF', fontSize: 14, fontFamily: FONTS.medium },
  filterBar: {
    padding: 12, borderRadius: 12, borderWidth: 1, gap: 10,
  },
  searchInput: {
    borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10,
    fontSize: 14, fontFamily: FONTS.regular, minHeight: 42,
  },
  filterChip: {
    paddingVertical: 6, paddingHorizontal: 12,
    backgroundColor: '#F1F5F9', borderRadius: 16,
  },
  filterChipActive: { backgroundColor: COLORS.primary },
  filterChipText: { fontSize: 12, fontFamily: FONTS.medium, color: '#475569' },
  filterChipTextActive: { color: '#FFFFFF' },
  emptyCard: {
    padding: 40, borderRadius: 12, borderWidth: 1, alignItems: 'center',
  },
  emptyText: { fontSize: 16, fontFamily: FONTS.regular },
  emptyHint: { fontSize: 13, marginTop: 8, fontFamily: FONTS.regular },
  itemCard: {
    flexDirection: 'row-reverse', alignItems: 'flex-start', gap: 12,
    padding: 16, borderRadius: 12, borderWidth: 1,
  },
  itemTitle: { fontSize: 16, fontFamily: FONTS.medium, textAlign: 'right' },
  itemDescription: { fontSize: 13, marginTop: 6, textAlign: 'right' },
  itemMeta: { fontSize: 12, fontFamily: FONTS.regular, marginTop: 2, textAlign: 'right' },
  contactItem: { fontSize: 12, fontFamily: FONTS.regular },
  usageCount: { fontSize: 12, marginTop: 8, textAlign: 'right' },
  typeBadge: {
    paddingVertical: 2, paddingHorizontal: 8, borderRadius: 6,
  },
  typeBadgeText: { fontSize: 11, fontFamily: FONTS.medium },
  inactiveBadge: {
    paddingVertical: 2, paddingHorizontal: 8,
    backgroundColor: '#FEE2E2', borderRadius: 6,
  },
  inactiveBadgeText: { fontSize: 11, fontFamily: FONTS.medium, color: '#991B1B' },
  editBtn: {
    paddingVertical: 6, paddingHorizontal: 12,
    backgroundColor: '#EFF6FF', borderRadius: 6,
  },
  editBtnText: { color: '#1E40AF', fontSize: 13, fontFamily: FONTS.medium },
  deleteBtn: {
    paddingVertical: 6, paddingHorizontal: 12,
    backgroundColor: '#FEF2F2', borderRadius: 6,
  },
  deleteBtnText: { color: '#B91C1C', fontSize: 13, fontFamily: FONTS.medium },
  modalOverlay: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center', alignItems: 'center', padding: 16,
  },
  modalCard: {
    width: '100%', maxWidth: 640, maxHeight: '90%',
    padding: 20, borderRadius: 12, borderWidth: 1,
  },
  modalTitle: {
    fontSize: 18, fontFamily: FONTS.medium, textAlign: 'right', marginBottom: 16,
  },
  formLabel: { fontSize: 13, fontFamily: FONTS.regular, textAlign: 'right', marginBottom: 6 },
  input: {
    borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10,
    fontSize: 14, fontFamily: FONTS.regular, minHeight: 44,
  },
  textarea: { minHeight: 80, paddingTop: 10 },
  typeSelector: {
    flexDirection: 'row-reverse', flexWrap: 'wrap', gap: 8,
  },
  typeOption: {
    paddingVertical: 8, paddingHorizontal: 12,
    backgroundColor: '#F1F5F9', borderRadius: 8,
    borderWidth: 1, borderColor: '#E2E8F0',
  },
  typeOptionActive: {
    backgroundColor: COLORS.primary, borderColor: COLORS.primary,
  },
  typeOptionText: { fontSize: 12, fontFamily: FONTS.medium, color: '#475569' },
  typeOptionTextActive: { color: '#FFFFFF' },
  activeToggle: {
    flexDirection: 'row-reverse', alignItems: 'center', gap: 10, marginTop: 4,
  },
  checkbox: {
    width: 22, height: 22, borderRadius: 4,
    borderWidth: 2, borderColor: '#D1D5DB',
    alignItems: 'center', justifyContent: 'center',
  },
  checkboxActive: {
    backgroundColor: COLORS.primary, borderColor: COLORS.primary,
  },
  checkboxMark: { color: '#FFFFFF', fontSize: 14, fontWeight: '700' },
  activeToggleLabel: { fontSize: 13, fontFamily: FONTS.regular },
  modalSaveBtn: {
    flex: 1, paddingVertical: 12, backgroundColor: COLORS.primary,
    borderRadius: 8, alignItems: 'center',
  },
  modalSaveBtnText: { color: '#FFFFFF', fontSize: 14, fontFamily: FONTS.medium },
  modalCancelBtn: {
    flex: 1, paddingVertical: 12, borderWidth: 1,
    borderRadius: 8, alignItems: 'center',
  },
  modalCancelBtnText: { fontSize: 14, fontFamily: FONTS.medium },
});
